import { type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import {
  DownloadTransPickupRequestDocument,
  type DownloadTransPickupRequestQuery,
  type DownloadTransPickupRequestQueryVariables,
  type TransPickupRequestDownloadInput,
  type OperationInput,
} from "@/generated/graphql";
import type { ApolloQueryResult } from "@apollo/client/core";
import { saveAs } from "file-saver";
import Encoding from "encoding-japanese";

interface UseDeliveryMoveDownloadParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  downloadingDialog: Ref<boolean>;
  listDataTotalCount: Ref<number>;
  searchConditionReceptionNo: Ref<string>;
  searchConditionReceptionDateFrom: Ref<Date | null>;
  searchConditionReceptionDateTo: Ref<Date | null>;
  searchConditionDeliveryDateFrom: Ref<Date | null>;
  searchConditionDeliveryDateTo: Ref<Date | null>;
  deliveryTypeValues: Ref<{ label: string; value: number }>;
  areaSelectValues: Ref<{ area_code: string; name: string }>;
  statusSelectedItem: Ref<string[]>;
  searchConditionChainCode: Ref<string[]>;
  searchShopSelectValuesFrom: Ref<string[]>;
  searchShopSelectValuesTo: Ref<string[]>;
  formatDateForDisplay: (date: Date | null) => string | null;
}

/**
 * 一覧データのダウンロード処理を提供する composable。
 *
 * @returns ダウンロード実行に必要な関数群
 */
export const useDeliveryMoveDownload = ({
  errdialog,
  errMessage,
  overlay,
  downloadingDialog,
  listDataTotalCount,
  searchConditionReceptionNo,
  searchConditionReceptionDateFrom,
  searchConditionReceptionDateTo,
  searchConditionDeliveryDateFrom,
  searchConditionDeliveryDateTo,
  deliveryTypeValues,
  areaSelectValues,
  statusSelectedItem,
  searchConditionChainCode,
  searchShopSelectValuesFrom,
  searchShopSelectValuesTo,
  formatDateForDisplay,
}: UseDeliveryMoveDownloadParams) => {
  /**
   * ダウンロード @Click
   */
  const onClickDownload = async (): Promise<void> => {
    if (listDataTotalCount.value <= 0) {
      return;
    }

    downloadingDialog.value = true;

    try {
      // graphql接続
      const apolloClient = await generateApolloClient();

      const itemsPerPage = 5000;
      const pageCount = Math.ceil(listDataTotalCount.value / itemsPerPage);

      // CSVデータ
      let csvData = "";

      for (let page = 1; page <= pageCount; page++) {
        // 在庫移動・回収依頼ダウンロード input
        const input: TransPickupRequestDownloadInput = {
          reception_no:
            searchConditionReceptionNo.value !== undefined &&
            searchConditionReceptionNo.value !== null &&
            searchConditionReceptionNo.value !== ""
              ? searchConditionReceptionNo.value
              : undefined,
          delivery_type:
            deliveryTypeValues.value !== undefined &&
            deliveryTypeValues.value !== null &&
            deliveryTypeValues.value.value !== 0
              ? deliveryTypeValues.value.value
              : undefined,
          reception_date_from:
            searchConditionReceptionDateFrom.value !== undefined &&
            searchConditionReceptionDateFrom.value !== null
              ? formatDateForDisplay(searchConditionReceptionDateFrom.value)
              : undefined,
          reception_date_to:
            searchConditionReceptionDateTo.value !== undefined &&
            searchConditionReceptionDateTo.value !== null
              ? formatDateForDisplay(searchConditionReceptionDateTo.value)
              : undefined,
          delivery_date_from:
            searchConditionDeliveryDateFrom.value !== undefined &&
            searchConditionDeliveryDateFrom.value !== null
              ? formatDateForDisplay(searchConditionDeliveryDateFrom.value)
              : undefined,
          delivery_date_to:
            searchConditionDeliveryDateTo.value !== undefined &&
            searchConditionDeliveryDateTo.value !== null
              ? formatDateForDisplay(searchConditionDeliveryDateTo.value)
              : undefined,
          area_code:
            areaSelectValues.value !== undefined &&
            areaSelectValues.value !== null &&
            areaSelectValues.value.area_code !== ""
              ? areaSelectValues.value.area_code
              : undefined,
          delivery_status:
            statusSelectedItem.value !== undefined &&
            statusSelectedItem.value !== null &&
            statusSelectedItem.value.length > 0
              ? statusSelectedItem.value
              : undefined,
          chain_code: localStorageGetChainCode(),
          chain_code_list: searchConditionChainCode.value,
          customer_code: localStorageGetCustomerCode(),
          shop_code_list:
            searchShopSelectValuesFrom.value.length > 0
              ? searchShopSelectValuesFrom.value
              : undefined,
          delivery_shop_code_list:
            searchShopSelectValuesTo.value.length > 0
              ? searchShopSelectValuesTo.value
              : undefined,
          limit: itemsPerPage,
          offset: (page - 1) * itemsPerPage,
        };

        // 在庫移動・回収依頼ダウンロード logInput
        const logInput: OperationInput = {
          area_code: "10",
          detail: "操作詳細",
          display_category: "画面名",
          operation: "操作",
          user_id: localStorageGetUserId(),
        };

        // 在庫移動・回収依頼ダウンロード [戻り値:CSVはBase64エンコード済み]
        let result: ApolloQueryResult<DownloadTransPickupRequestQuery>;
        try {
          result = await apolloClient.query<
            DownloadTransPickupRequestQuery,
            DownloadTransPickupRequestQueryVariables
          >({
            query: DownloadTransPickupRequestDocument,
            variables: { input, logInput },
            fetchPolicy: "no-cache",
          });
        } catch (err: unknown) {
          if (err instanceof Error) {
            console.error(err);
          } else {
            console.error("未知のエラー:", err);
          }
          overlay.value = false;
          errdialog.value = true;
          errMessage.value = "在庫移動・回収依頼のダウンロードに失敗しました。";
          return;
        }

        if (
          result.data.downloadTransPickupRequest === undefined ||
          result.data.downloadTransPickupRequest === null
        ) {
          // 在庫移動・回収依頼ダウンロード エラー
          console.error(
            "result.data.downloadTransPickupRequest is undefined or null"
          );
          return;
        }

        // 在庫移動・回収依頼ダウンロード データ処理
        const json: { csv: string } = JSON.parse(
          result.data.downloadTransPickupRequest
        ) as { csv: string };

        // Base64デコード（ブラウザ互換）
        // BOM (U+FEFF) が先頭にあれば削除
        const tempCsvData = decodeURIComponent(
          atob(json.csv)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join("")
        ).replace(/^\uFEFF/, "");

        // 分割されたCSVデータを結合
        if (csvData === "") {
          // 配達予定日データをDSSと連携されるまで非表示にするST
          const lineArray = tempCsvData.split(/\r/);

          for (let i = 0; i < lineArray.length - 1; i++) {
            const curLineData = lineArray[i].split(/,/);
            curLineData.splice(20, 1);
            csvData += curLineData.join(",") + "\n";
          }
          // 配達予定日データをDSSと連携されるまで非表示にする(連携されたら当該コードを削除して下記のコメント行を復帰)ED
          //csvData = tempCsvData;
        } else {
          // タイトル行を削除
          // そのために改行で分割
          const lineArray = tempCsvData.split(/\r/);
          for (let i = 1; i < lineArray.length - 1; i++) {
            // 「タイトル行」と「末尾の余分な行」を除いた行を追加

            // 配達予定日データをDSSと連携されるまで非表示にするST
            const curLineData = lineArray[i].split(/,/);
            curLineData.splice(20, 1);
            csvData += curLineData.join(",") + "\n";
            // 配達予定日データをDSSと連携されるまで非表示にする(連携されたら当該コードを削除して下記のコメント行を復帰)ED
            // csvData += lineArray[i] + "\n";
          }
        }
      }

      // Shift-JISにエンコード
      const unicodeArray = Encoding.stringToCode(csvData);
      const sjisArray = Encoding.convert(unicodeArray, {
        to: "SJIS",
        from: "UNICODE",
      });
      const uint8Array = new Uint8Array(sjisArray);

      // ダウンロードファイル作成
      const blob = new Blob([uint8Array], {
        type: "text/csv",
      });

      // ダウンロード
      saveAs(blob, "delivery_move.csv");
    } finally {
      downloadingDialog.value = false;
    }
  };

  return {
    onClickDownload,
  };
};
